import * as React from "react";
import {
  Tab as ReactTab,
  TabList as ReactTabList,
  Tabs as ReactTabs,
} from "react-tabs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { createBemBlockBuilder } from "@ebsco-ui/utils";
import ScrollControl, { ScrollControlTypes } from "./scroll-control";
import { AXIS, makeScroller } from "./smooth-scroll";
import {
  getIsScrollingAllowed,
  getNextScrollingPosition,
} from "./scrolling-helpers";
import TabPanel from "./tab-panel";
import { TabsContextProvider } from "./tabs-context";

import "./tabs.scss";

export interface TabProps {
  title: string;
  disabled?: boolean;
  icon?: IconProp;
}

export interface TabsProps {
  children: React.ReactNode;
  className?: string;
  defaultIndex?: number;
  selectedIndex?: number;
  disableTabs?: boolean;
  isTabRequired?: boolean;
  i18n: {
    scrollControlStartTitle: string;
    scrollControlEndTitle: string;
  };
  isRTL?: boolean;
  tabs: Array<TabProps>;
  onSelect(index: number, lastIndex: number, event: Event): void;
}

export interface TabsState {
  isScrollingAllowed: {
    start: boolean;
    end: boolean;
  };
}

const TAB_LIST_CLASSNAME = "eb-tabs__tab-list";
const ANIMATION_SCROLL_RATE = 5;

class Tabs extends React.Component<TabsProps, TabsState> {
  static TabPanel = TabPanel;
  static BASE_CLASSNAME = "eb-tabs";

  static defaultProps = {
    defaultIndex: null,
    selectedIndex: null,
    disableTabs: false,
    isRTL: false,
    isTabRequired: false,
  };

  getBemBlocksWith = createBemBlockBuilder([
    Tabs.BASE_CLASSNAME,
    this.props.className,
  ]);

  smoothScroll = makeScroller();

  state = {
    isScrollingAllowed: { start: false, end: false },
  };

  componentDidMount() {
    this.updateScrollPermissions();
    window.addEventListener("resize", this.updateScrollPermissions);
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.updateScrollPermissions);
  }

  getTabList = () => {
    return document.getElementsByClassName(TAB_LIST_CLASSNAME)[0];
  };

  updateScrollPermissions = () => {
    this.setState({ isScrollingAllowed: this.isScrollingAllowed() });
  };

  isScrollingAllowed = () => {
    const { isRTL } = this.props;
    const element = this.getTabList();

    // tablist may not exist when there is just one tab or tabs are disabled
    if (element) {
      return getIsScrollingAllowed({ element, isRTL });
    } else {
      return {
        start: false,
        end: false,
      };
    }
  };

  handleScroll = (type: ScrollControlTypes) => {
    const { isRTL } = this.props;
    const tabList = this.getTabList();
    const nextScrollingPosition = getNextScrollingPosition({
      element: tabList,
      isRTL,
      type,
    });

    this.smoothScroll({
      container: tabList,
      targetScrollPosition: nextScrollingPosition,
      axis: AXIS.h,
      scrollRate: ANIMATION_SCROLL_RATE,
    });
  };

  renderTab = ({ icon, title, disabled }: TabProps, index: number) => {
    return (
      <ReactTab key={`tab-${index}`} disabled={disabled}>
        {icon && <FontAwesomeIcon icon={icon} />}
        {title}
      </ReactTab>
    );
  };

  render() {
    const {
      children,
      className,
      disableTabs,
      i18n: { scrollControlStartTitle, scrollControlEndTitle },
      isRTL,
      tabs,
      isTabRequired,
      ...rest
    } = this.props;
    const {
      isScrollingAllowed: { start, end },
    } = this.state;

    const isDisabled = disableTabs || (tabs.length < 2 && !isTabRequired);

    const tabsContext = {
      isDisabled,
      getBemBlocksWith: this.getBemBlocksWith,
    };

    if (isDisabled) {
      return (
        <TabsContextProvider value={tabsContext}>
          <div className={this.getBemBlocksWith()}>{children}</div>
        </TabsContextProvider>
      );
    }

    return (
      <TabsContextProvider value={tabsContext}>
        <ReactTabs {...rest} className={this.getBemBlocksWith()}>
          <div className={this.getBemBlocksWith("__tab-list-wrapper")}>
            <ScrollControl
              className={this.getBemBlocksWith("__scroll-control")}
              isEnabled={start}
              isRTL={isRTL}
              title={scrollControlStartTitle}
              type={ScrollControlTypes.start}
              onClick={this.handleScroll}
            />
            <ReactTabList
              data-auto="tab-list"
              onScroll={this.updateScrollPermissions}
              className={this.getBemBlocksWith("__tab-list")}
            >
              {tabs.map(this.renderTab)}
            </ReactTabList>
            <ScrollControl
              className={this.getBemBlocksWith("__scroll-control")}
              isEnabled={end}
              isRTL={isRTL}
              title={scrollControlEndTitle}
              type={ScrollControlTypes.end}
              onClick={this.handleScroll}
            />
          </div>
          {children}
        </ReactTabs>
      </TabsContextProvider>
    );
  }
}

export default Tabs;
